<template>
  <section
    class="pb-space-lg pb-lg-space-xxl bg-light-1 position-relative"
    :class="{ 'pt-space-lg py-lg-space-xxl': sectionPaddingTop }"
  >
    <div
      v-if="displayBgPattern"
      class="background-pattern"
      :class="backgroundPatternClass"
      :data-background-image="backgroundStyle"
    ></div>
    <div class="container position-relative">
      <div class="row">
        <div
          class="col-12 col-lg-6 order-1 mb-space-sm mb-lg-0"
          :class="{ 'order-lg-2': slotPosition === 'left' }"
        >
          <div
            class="px-space-sm py-space-lg p-lg-space-xl px-xl-space-xxl py-xl-space-xxxl border mb-space-sm mb-lg-0 h-100 bg-white"
          >
            <h2 class="h2-medium text-dark-3 mb-space-sm me-lg-space-sm">
              {{ title }}
            </h2>
            <h3 class="h3-medium text-dark-3 mb-space-md">{{ subtitle }}</h3>
            <div class="mb-space-md" v-html="description"></div>
            <NuxtLink
              :href="link.uri!"
              :target="link.target"
              class="btn btn-dark-3"
              @click="handleLinkClick"
              >{{ link.text }}
            </NuxtLink>
          </div>
        </div>
        <div class="col-12 col-lg-6 order-2" :class="{ 'order-lg-1': slotPosition === 'left' }">
          <div class="border h-100" :class="boxColorClass">
            <slot name="content"></slot>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script lang="ts" setup>
import { analyticsComposable } from "@rds-vue-ui/analytics-gs-composable";
import type { ParagraphHtmlLink } from "~/server/resolvers/resolvers-types";
import type { VariantOption } from "~/types/types";

interface Props {
  title: string;
  subtitle: string;
  description: string;
  slotPosition?: "left" | "right";
  slotBgVariant?: VariantOption;
  link: ParagraphHtmlLink;
  sectionPaddingTop?: boolean;
  displayBgPattern?: boolean;
  bgPatternSrc?: string;
}

const props = withDefaults(defineProps<Props>(), {
  slotPosition: "right",
  slotBgVariant: "light-4",
  sectionPaddingTop: true,
  displayBgPattern: false
});

const backgroundPatternClass = computed(() => {
  let classList = "";
  classList += props.displayBgPattern ? "rds-lazy" : "";
  return classList;
});
const backgroundStyle = computed(() => {
  return props.displayBgPattern ? props.bgPatternSrc : null;
});
const boxColorClass = computed(() => {
  return `bg-${props.slotBgVariant}`;
});

const handleLinkClick = () => {
  analyticsComposable.trackLinkEvent(
    props.link.uri!.includes("asu.edu") ? "internal link" : "external link",
    "main content",
    props.title.toLowerCase(),
    props.link.text!.toLowerCase(),
    "section-services-grid"
  );
};
</script>

<style lang="scss" scoped>
.background-pattern {
  position: absolute;
  left: 0;
  bottom: 0;
  height: 60%;
  width: 100%;
  background-size: auto;
  background-position: top;
}
@media screen and (min-width: 992px) {
  .image-box {
    min-height: 500px;
    background-size: contain;
    background-position: center top;
    background-repeat: no-repeat;
  }
}
@media screen and (min-width: 1200px) {
  .image-box {
    min-height: 600px;
  }
}
</style>
